import React, { useEffect, useState, useCallback, useRef } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Container, Form, Image, Spinner } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";

import "bootstrap/dist/css/bootstrap.min.css";
import "./signin.css";

import backarrow from "../../assets/images/backarrow.jpg";
import logo2 from "../../assets/images/logo2.png";
import responsivebg from "../../assets/images/responsive-bg.png";

import { useCookies } from "../../hooks/useCookies";
import { useAppContext } from "../../Context/UseContext";
import IconButton from "../userflow/components/card/IconButton";
import * as authAction from "../../actions/auth";
import { useGeolocation, DEFAULT_COUNTRY } from "../../utils/geolocationUtils.jsx";
import { useRateLimit } from "../../hooks/useRateLimit";

export const SignIn = () => {
  const recaptchaRef = useRef(null);

  const navigate = useNavigate();
  const { setCookie } = useCookies();
  const searchParams = useSearchParams();
  const { detectCountry, isLoading: geoLoading } = useGeolocation();

  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  const { phoneNumber, setPhoneNumber } = useAppContext();

  // Rate limiting hook
  const {
    isRateLimited,
    timeRemaining,
    handleRateLimitError,
    formatTimeRemaining,
    getRateLimitMessage
  } = useRateLimit('signin');


  // Geolocation effect
  useEffect(() => {
    let isMounted = true;

    const initializeCountry = async () => {
      try {
        const detectedCountry = await detectCountry();
        if (detectedCountry && isMounted) {
          setSelectedCountry(detectedCountry);
        }
      } catch (error) {
        console.warn("Failed to initialize country detection:", error);
      }
    };

    initializeCountry();

    return () => {
      isMounted = false;
    };
  }, [detectCountry]);


  const handlePhoneNumberChange = useCallback((value) => {
    setPhoneNumber(value || "");
    setErrorMessage(""); // Clear error when user starts typing
  }, [setPhoneNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSendingOtp || isRateLimited) return;

    // Validate phone number
    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
      setErrorMessage("*Please enter a valid phone number.");
      return;
    }

    setIsSendingOtp(true);
    setErrorMessage("");

    try {
      recaptchaRef.current.reset();
      const token = await recaptchaRef.current.executeAsync();

      const response = await authAction.loginPhoneOTP({
        phone: phoneNumber,
        recaptchaToken: token
      });
      if (response && response.status === true) {
        setCookie("phoneNumber", phoneNumber, 7);
        const nextRoute = searchParams[0].get("next") || "signinphoneotp";
        navigate(`/${nextRoute}`);
      } else {
        // Check for rate limit error
        if (handleRateLimitError(response)) {
          setErrorMessage(getRateLimitMessage());
        } else {
          setErrorMessage(response?.response?.data?.message || response?.message || "*Failed to send code.");
        }
      }
    } catch (error) {
      console.error("OTP error:", error);
      setErrorMessage("*An error occurred. Please try again later.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleBackNavigation = useCallback(() => {
    window.history.back();
  }, []);


  return (
    <div className="signupphone-container" role="main">
      <div className="signup-phone-bg">
        <Image
          className="responsive-bg"
          src={responsivebg}
          alt="Background"
          loading="lazy"
        />
      </div>

      <Container className="signupphone-main">
        <Container className="signupphone-content">
          <Container className="logo-progressbar1">
            <Container className="logo-arrow1">
              <Image
                src={backarrow}
                className="backarrow"
                onClick={handleBackNavigation}
                alt="Go back"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleBackNavigation();
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
              <Image
                src={logo2}
                alt="Logo"
                className="logo1"
                style={{ backgroundColor: "transparent" }}
                loading="lazy"
              />
            </Container>
          </Container>

          <Container className="verify-phone-text">
            <IconButton
              type="phone"
              onClick={(e) => e.stopPropagation()}
              aria-label="Phone icon"
            />
          </Container>

          <Container className="verify-phone-details">
            <Form onSubmit={handleSubmit} className="verify-phone-form">
              <Form.Group
                className="verify-form-group"
              >
                <Form.Label className="num-verify-lable" htmlFor="phoneInput">
                  What's your phone number?
                </Form.Label>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <PhoneInput
                    international
                    withCountryCallingCode
                    defaultCountry={selectedCountry}
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    className={`num-verify-input-container ${errorMessage ? "error" : ""}`}
                    disabled={isSendingOtp}
                  />
                </div>
                {errorMessage && (
                  <Form.Text
                    id="phoneError"
                    className="text-danger error-message"
                    style={{ marginTop: "5px" }}
                    role="alert"
                  >
                    {errorMessage}
                    {isRateLimited && timeRemaining > 0 && (
                      <div style={{ marginTop: "5px", fontWeight: "bold" }}>
                        Time remaining: {formatTimeRemaining(timeRemaining)}
                      </div>
                    )}
                    {errorMessage.includes("Try creating a new account") && (
                      <a href="./signup" className="signup-signin">
                        {" "}
                        here.
                      </a>
                    )}
                  </Form.Text>
                )}
              </Form.Group>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                size="invisible"
                badge="bottomright"
                style={{
                  visibility: 'hidden',
                  position: 'absolute',
                  left: '-9999px'
                }}
                onErrored={() => console.log("reCAPTCHA errored")}
                onExpired={() => console.log("reCAPTCHA expired")}
              />
              <button
                type="submit"
                className="global-next-btn"
                disabled={isSendingOtp || geoLoading || isRateLimited}
                aria-describedby={isSendingOtp ? "loadingStatus" : undefined}
                title={geoLoading ? "Detecting your location..." : isRateLimited ? "Please wait before trying again" : ""}
              >
                {isSendingOtp ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : isRateLimited ? (
                  `Try again in ${formatTimeRemaining(timeRemaining)}`
                ) : (
                  "Next"
                )}
              </button>
            </Form>
          </Container>
        </Container>
      </Container>


    </div>
  );
};
